import { call } from '@advanza/api'
import { InputField } from '@advanza/input'
import { Button, Icon } from '@advanza/ui'
import Card from 'components/Card'
import DemandNativeAppDormantTable from 'components/misc/DemandNativeAppDormantTable'
import DemandNativeAppUninstallTable from 'components/misc/DemandNativeAppUninstallTable'
import DemandNativeAppUserTable from 'components/misc/DemandNativeAppUserTable'
import { useEffect, useState } from 'react'

interface Summary {
    total_installed_users: number
    total_native_users: number
    push_opt_in_rate: number
    total_devices: number
    active_24h: number
    active_7d: number
    active_30d: number
    dormant_30d: number
    uninstalls_30d: number
    total_notifications_sent: number
    total_notifications_shown: number
    total_notifications_clicked: number
    notification_open_rate: number
    avg_open_time_seconds: number | null
}

interface ScreenView {
    screen_name: string
    view_count: number
}

interface EventCount {
    event_type: string
    event_count: number
}

interface Report {
    date_range: { start: string; end: string } | null
    summary: Summary
    behavioral: {
        top_screens: ScreenView[]
        event_distribution: EventCount[]
    }
}

const formatSeconds = (s: number | null): string => {
    if (s === null) return '–'
    if (s < 60) return `${Math.round(s)}s`
    if (s < 3600) {
        const m = Math.floor(s / 60)
        const sec = Math.round(s % 60)
        return sec > 0 ? `${m}m ${sec}s` : `${m}m`
    }
    const h = Math.floor(s / 3600)
    const m = Math.round((s % 3600) / 60)
    return m > 0 ? `${h}h ${m}m` : `${h}h`
}

const DemandNativeAppAnalyticsMisc = () => {
    const [start, setStart] = useState('')
    const [end, setEnd] = useState('')
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')
    const [report, setReport] = useState<Report | null>(null)
    const [refreshKey, setRefreshKey] = useState(0)

    const loadReport = async ({ bypassCache = false }: { bypassCache?: boolean } = {}) => {
        setLoading(true)
        setError('')
        setReport(null)
        try {
            const params = new URLSearchParams()
            if (start) params.set('start', start)
            if (end) params.set('end', end)
            if (bypassCache) params.set('bypass_cache', '1')
            // Cache-buster: keeps the URL unique so no CDN/browser cache can
            // serve a stale body for this live dashboard.
            params.set('_', String(Date.now()))
            const url = `office/misc/demand-native-app-analytics?${params.toString()}`
            const response = await call(url)
            if (response?.status === 'success' && response?.summary) {
                setReport(response as unknown as Report)
            } else {
                setError(response?.error || 'Failed to load report')
            }
        } catch (err) {
            setError('Error: ' + (err as Error).message)
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        loadReport()
    }, [])

    return (
        <div className="space-y-5">
            <div className="flex flex-wrap items-center gap-3">
                <InputField
                    type="date"
                    placeholder="Start (optional)"
                    value={start}
                    onChange={(e) => setStart(e.target.value)}
                />
                <InputField
                    type="date"
                    placeholder="End (optional)"
                    value={end}
                    onChange={(e) => setEnd(e.target.value)}
                />
                <Button
                    onClick={() => {
                        setRefreshKey((k) => k + 1)
                        loadReport({ bypassCache: true })
                    }}
                    disabled={loading}
                    before={<Icon name="refresh" />}>
                    {loading ? 'Loading…' : 'Load / Refresh'}
                </Button>
            </div>

            {error && (
                <div className="rounded-lg bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-md">
                    {error}
                </div>
            )}

            {loading && !report && (
                <div className="flex flex-col items-center justify-center py-12">
                    <div className="h-8 w-8 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
                    <p className="mt-3 text-md text-neutral-500">Loading demand app analytics…</p>
                </div>
            )}

            {report && (
                <div className="space-y-5">
                    {report.date_range && (
                        <p className="text-md text-neutral-500">
                            Date range:{' '}
                            <span className="font-medium text-neutral-700">
                                {report.date_range.start}
                            </span>
                            {' → '}
                            <span className="font-medium text-neutral-700">
                                {report.date_range.end}
                            </span>
                        </p>
                    )}

                    <Card useToggle header="Summary (KPIs)">
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                            <div
                                className="rounded-lg bg-slate-50 p-4 text-center ring-1 ring-slate-200/50"
                                title="Users with the app: either the install flag is set or they have a push token. Superset of push-enabled users; every one is listed in the per-user table below.">
                                <div className="text-2xl font-bold text-blue-600 tabular-nums">
                                    {report.summary.total_installed_users}
                                </div>
                                <div className="text-sm font-medium text-slate-500 mt-0.5">
                                    Installed users
                                </div>
                            </div>
                            <div
                                className="rounded-lg bg-slate-50 p-4 text-center ring-1 ring-slate-200/50"
                                title="Installed users that also registered at least one push token (a subset of installed users).">
                                <div className="text-2xl font-bold text-blue-600 tabular-nums">
                                    {report.summary.total_native_users}
                                </div>
                                <div className="text-sm font-medium text-slate-500 mt-0.5">
                                    Push-enabled users
                                </div>
                            </div>
                            <div className="rounded-lg bg-slate-50 p-4 text-center ring-1 ring-slate-200/50">
                                <div className="text-display-xs font-bold text-slate-700 tabular-nums">
                                    {report.summary.push_opt_in_rate}%
                                </div>
                                <div className="text-sm font-medium text-slate-500 mt-0.5">
                                    Push opt-in rate
                                </div>
                            </div>
                            <div
                                className="rounded-lg bg-slate-50 p-4 text-center ring-1 ring-slate-200/50"
                                title="Distinct physical push devices across push-enabled users. Can exceed push-enabled users (multiple devices) and is unrelated to the installed-user count.">
                                <div className="text-2xl font-bold text-blue-600 tabular-nums">
                                    {report.summary.total_devices}
                                </div>
                                <div className="text-sm font-medium text-slate-500 mt-0.5">
                                    Devices
                                </div>
                            </div>
                            <div className="rounded-lg bg-slate-50 p-4 text-center ring-1 ring-slate-200/50">
                                <div className="text-2xl font-bold text-emerald-600 tabular-nums">
                                    {report.summary.active_24h}
                                </div>
                                <div className="text-sm font-medium text-slate-500 mt-0.5">
                                    Active 24h
                                </div>
                            </div>
                            <div className="rounded-lg bg-slate-50 p-4 text-center ring-1 ring-slate-200/50">
                                <div className="text-2xl font-bold text-teal-600 tabular-nums">
                                    {report.summary.active_7d}
                                </div>
                                <div className="text-sm font-medium text-slate-500 mt-0.5">
                                    Active 7d
                                </div>
                            </div>
                            <div className="rounded-lg bg-slate-50 p-4 text-center ring-1 ring-slate-200/50">
                                <div className="text-2xl font-bold text-violet-600 tabular-nums">
                                    {report.summary.active_30d}
                                </div>
                                <div className="text-sm font-medium text-slate-500 mt-0.5">
                                    Active 30d
                                </div>
                            </div>
                            <div className="rounded-lg bg-slate-50 p-4 text-center ring-1 ring-slate-200/50">
                                <div className="text-2xl font-bold text-amber-600 tabular-nums">
                                    {report.summary.dormant_30d}
                                </div>
                                <div className="text-sm font-medium text-slate-500 mt-0.5">
                                    Dormant 30d
                                </div>
                            </div>
                            <div className="rounded-lg bg-slate-50 p-4 text-center ring-1 ring-slate-200/50">
                                <div className="text-2xl font-bold text-rose-600 tabular-nums">
                                    {report.summary.uninstalls_30d}
                                </div>
                                <div className="text-sm font-medium text-slate-500 mt-0.5">
                                    Uninstalls 30d
                                </div>
                            </div>
                            <div className="rounded-lg bg-slate-50 p-4 text-center ring-1 ring-slate-200/50">
                                <div className="text-2xl font-bold text-blue-600 tabular-nums">
                                    {report.summary.total_notifications_sent}
                                </div>
                                <div className="text-sm font-medium text-slate-500 mt-0.5">
                                    Notif. sent
                                </div>
                            </div>
                            <div className="rounded-lg bg-slate-50 p-4 text-center ring-1 ring-slate-200/50">
                                <div className="text-2xl font-bold text-emerald-600 tabular-nums">
                                    {report.summary.total_notifications_clicked}
                                </div>
                                <div className="text-sm font-medium text-slate-500 mt-0.5">
                                    Notif. opened ({report.summary.notification_open_rate}%)
                                </div>
                            </div>
                            <div className="rounded-lg bg-slate-50 p-4 text-center ring-1 ring-slate-200/50">
                                <div className="text-xl font-bold text-slate-700">
                                    {formatSeconds(report.summary.avg_open_time_seconds)}
                                </div>
                                <div className="text-sm font-medium text-slate-500 mt-0.5">
                                    Avg notif. open time
                                </div>
                            </div>
                        </div>
                    </Card>

                    <Card useToggle header="Per user">
                        <p className="text-md text-neutral-500 mb-3">
                            Every user with the app installed. Users with a push token show their
                            devices and notification stats; install-flag-only users appear with zero
                            devices. Row count matches "Installed users" above.
                        </p>
                        <DemandNativeAppUserTable start={start} end={end} refreshKey={refreshKey} />
                    </Card>

                    <Card useToggle header="Behavioral data">
                        <div className="grid gap-6 sm:grid-cols-1 lg:grid-cols-2">
                            <div className="rounded-lg border border-neutral-200 bg-white p-4">
                                <h4 className="text-md font-semibold text-neutral-700 mb-3">
                                    Top screens
                                </h4>
                                {report.behavioral.top_screens.length === 0 ? (
                                    <p className="text-md text-neutral-500">
                                        No screen view data yet
                                    </p>
                                ) : (
                                    <div className="max-h-64 overflow-y-auto">
                                        <table className="w-full text-md">
                                            <tbody>
                                                {report.behavioral.top_screens.map((s) => (
                                                    <tr
                                                        key={s.screen_name}
                                                        className="border-t border-neutral-100 first:border-t-0">
                                                        <td className="py-1.5 text-neutral-700 truncate">
                                                            {s.screen_name}
                                                        </td>
                                                        <td className="py-1.5 pl-4 text-right font-bold tabular-nums text-neutral-900 whitespace-nowrap">
                                                            {s.view_count}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                            <div className="rounded-lg border border-neutral-200 bg-white p-4">
                                <h4 className="text-md font-semibold text-neutral-700 mb-3">
                                    Event distribution
                                </h4>
                                {report.behavioral.event_distribution.length === 0 ? (
                                    <p className="text-md text-neutral-500">No event data yet</p>
                                ) : (
                                    <div className="flex flex-wrap gap-2">
                                        {report.behavioral.event_distribution.map((e) => (
                                            <span
                                                key={e.event_type}
                                                className="inline-flex items-center rounded-md px-3 py-1.5 text-md font-medium bg-neutral-100 text-neutral-800 ring-1 ring-inset ring-neutral-200/50">
                                                <span>{e.event_type}</span>
                                                <span className="ml-1.5 font-bold tabular-nums">
                                                    {e.event_count}
                                                </span>
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </Card>

                    <Card useToggle header="Dormant (stopped using app)">
                        <DemandNativeAppDormantTable refreshKey={refreshKey} />
                    </Card>

                    <Card useToggle header="Uninstalls detected">
                        <DemandNativeAppUninstallTable refreshKey={refreshKey} />
                    </Card>
                </div>
            )}
        </div>
    )
}

export default DemandNativeAppAnalyticsMisc
